import { useFlag } from '@reflag/react-sdk';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { getWorkDomainFromEmail } from '@gitbook/app-database';
import type { NavigationScreenWelcome } from '@gitbook/app-navigation';
import { isPricingFeatureAvailable } from '@gitbook/app-pricing';
import { GitBookLoader, toast } from '@gitbook/spine-react';

import { SITE_SETUP_GUIDE_STARTER, useSiteSetupConversation } from '../../Components';
import { useNavigation } from '../../navigation';
import {
    apiClient,
    clearOnboardingParam,
    getOnboardingParam,
    OnboardingParamName,
    useLoggedInUser,
} from '../../state';
import { SiteOnboardingScreen } from './SiteOnboardingScreen';
import { FLOW_STEPS, type PendingOrganization, useFlowContext } from './useFlowContext';
import { WelcomeLayout } from './WelcomeLayout';
import WelcomeScreen, { syncCreatedOrganizationQueries } from './WelcomeScreen';

/**
 * First screen as part of the user onboarding.
 * Also used when creating an organization.
 */
export default function OnboardingFlow(props: {
    screen: NavigationScreenWelcome;
}): React.ReactElement | null {
    const { screen } = props;
    const [state, setStep] = useFlowContext();
    const user = useLoggedInUser();
    const [draftTitle, setDraftTitle] = React.useState<string | undefined>();

    let title: string | undefined;
    let content: React.ReactNode = null;

    switch (state.step) {
        case FLOW_STEPS.CREATE_ORGANIZATION:
            title = draftTitle;
            content = (
                <WelcomeScreen
                    screen={screen}
                    onNext={({ organization, siteId, hasBootstrappedContent }) => {
                        setStep({
                            step: FLOW_STEPS.COMPLETE,
                            context: { organization, siteId, hasBootstrappedContent },
                        });
                    }}
                    onNextSiteOnboarding={(pendingOrganization) => {
                        setStep({
                            step: FLOW_STEPS.SITE_ONBOARDING,
                            context: { pendingOrganization },
                        });
                    }}
                    onTitleChange={setDraftTitle}
                />
            );
            break;

        case FLOW_STEPS.SITE_ONBOARDING:
            title = state.context.pendingOrganization.title;
            content = (
                <SiteOnboardingScreen
                    onCreateSite={(preferences) =>
                        createOrganizationAndSite({
                            pendingOrganization: state.context.pendingOrganization,
                            email: user.email,
                            preferences,
                        })
                    }
                    onGoToSite={(result) => {
                        setStep({ step: FLOW_STEPS.COMPLETE, context: result });
                    }}
                />
            );
            break;

        case FLOW_STEPS.COMPLETE:
            assertOrgAndSiteCreated(state.context);
            title = state.context.organization.title;

            content = (
                <RedirectOnOrgCreated
                    organization={state.context.organization}
                    siteId={state.context.siteId}
                />
            );
            break;

        default:
            return null;
    }

    return (
        <WelcomeLayout title={title}>
            <React.Suspense fallback={<WelcomeLoader />}>{content}</React.Suspense>
        </WelcomeLayout>
    );
}

/** Create the organization (with its default site) now that site onboarding is done. */
async function createOrganizationAndSite({
    pendingOrganization,
    email,
    preferences,
}: {
    pendingOrganization: PendingOrganization;
    email: string | null | undefined;
    preferences: api.SiteOnboardingPreferences;
}): Promise<{
    organization: api.Organization;
    siteId: string;
    hasBootstrappedContent?: boolean;
} | null> {
    const emailDomain = email ? getWorkDomainFromEmail(email) : null;

    return (
        (await toast.catch(async () => {
            const openapiOnboardingParam = getOnboardingParam(OnboardingParamName.OPENAPI);

            const { data: organization } = await apiClient.organizations.createOrganization(
                {
                    title: pendingOrganization.title,
                    emailDomains:
                        emailDomain && pendingOrganization.emailDomainEnabled ? [emailDomain] : [],
                    onboardingPreferences: preferences,
                },
                {
                    withDefaultSite: true,
                    ...(openapiOnboardingParam ? { openapiUrl: openapiOnboardingParam } : {}),
                }
            );

            const siteId =
                organization.defaultContent?.type === 'site'
                    ? organization.defaultContent.site
                    : undefined;

            if (!siteId) {
                throw new Error('Site must be created');
            }

            // Add the new org to the cache before navigating because the API subscription
            // may not have refreshed the organization cached list yet.
            await syncCreatedOrganizationQueries(organization);

            clearOnboardingParam(OnboardingParamName.OPENAPI);

            return {
                organization,
                siteId,
                hasBootstrappedContent: Boolean(openapiOnboardingParam),
            };
        }, 'Error while creating organization')) ?? null
    );
}

function assertOrgAndSiteCreated({
    organization,
    siteId,
}: {
    organization: api.Organization;
    siteId: string;
}) {
    if (!organization) {
        throw new Error('Could not create organization');
    }

    if (!siteId) {
        throw new Error('Site must be created');
    }
}

/**
 * Land in the new organization: on its site's agent screen, with the setup guide started, under
 * `SITE_AI_SETUP_LANDING`, and on the site's overview otherwise. Shows the loader until then.
 */
function RedirectOnOrgCreated(props: { organization: api.Organization; siteId: string }) {
    const { organization, siteId } = props;
    const organizationId = organization.id;
    const { navigate } = useNavigation();
    const landingFlag = useFlag('SITE_AI_SETUP_LANDING');
    const setupFlag = useFlag('SITE_AI_SETUP');
    const isLoading = landingFlag.isLoading || setupFlag.isLoading;
    // What the agent screen requires too, or it would send the user back to the site.
    const landsOnAgent =
        landingFlag.isEnabled &&
        setupFlag.isEnabled &&
        isPricingFeatureAvailable(organization, { featureKey: api.OrganizationFeature.AiLens });
    const setupConversation = useSiteSetupConversation({
        organizationId,
        siteId: landsOnAgent ? siteId : undefined,
    });
    // Once, since the setup conversation is a new object on every render until the page changes.
    const redirected = React.useRef(false);

    React.useLayoutEffect(() => {
        if (redirected.current || isLoading) {
            return;
        }
        if (!landsOnAgent) {
            redirected.current = true;
            navigate(
                { screen: { type: 'site', organization: organizationId, site: siteId } },
                { replace: true }
            );
            return;
        }
        if (setupConversation) {
            redirected.current = true;
            setupConversation.sendPromptOnOpen(SITE_SETUP_GUIDE_STARTER.prompt);
            navigate(
                {
                    screen: {
                        type: 'agent',
                        organization: organizationId,
                        site: siteId,
                        conversation: setupConversation.id,
                    },
                },
                { replace: true }
            );
        }
    }, [isLoading, landsOnAgent, setupConversation, navigate, organizationId, siteId]);

    return <WelcomeLoader />;
}

function WelcomeLoader() {
    return (
        <div className="flex min-h-80 w-full flex-col items-center justify-center gap-4">
            <GitBookLoader />
            <div className="text-ui-heading-small">Preparing your onboarding...</div>
        </div>
    );
}
